import { useState, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { SidebarTalentSearchFiltersHubProps } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';
import { useFiltersHub } from '@hooks/filters/useFiltersHub';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { dataTestId } from '@services/data-test-id';
import { SidebarGroup } from '../partials/SidebarGroup';
import {
  LocationForm, CareerLevelsForm, LastActivityForm, StudyLevelsForm,
  DomainsForm, SpokenLanguagesForm, SearchInForm, ConfineResultsForm,
} from '../forms';
import { defaultFiltersCompany, mergeFilterValues } from '../helpers/functions';


/**
 * @description The hub for the sidebar company filters.
 * * **queryParams** - the query params for the filters.
 * * **setQueryParams** - the function to set the query params.
 * * **customFields** - if you want something else than the default filters.
 * * **customFormsProps** - the custom props for the forms (mostly spacing).
 * * **toggleDrawer** - the function to toggle the drawer.
 * * **jobBasicInfo** - the basic info for the job (for applicants list); optional.
 */
export const SidebarTalentSearchFiltersHub = (props: SidebarTalentSearchFiltersHubProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    customFields,
    customFormsProps,
    toggleDrawer,
    jobBasicInfo,
  } = props;

  // Show chosen filters
  const showFilters = customFields || defaultFiltersCompany;

  // Translation
  const { t } = useTranslation('common');

  // Show details for filter groups
  const [showDetails, setShowDetails] = useState(false);


  // FILTERS HUB HOOK
  // ***********************************************
  const {
    formRef,
    handleStep,
    record,
    setFormValues,
    submitForm,
    resetAllFilters,
    talentSearchLabels,
  } = useFiltersHub({
    queryParams,
    setQueryParams,
    externalFieldsList: ['keyword', 'sort', 'status', 'profileCategory'],
    protectFieldsOnMount: ['similarSearch'],
  });

  // Render component
  // ***********************************************
  return (
    <form noValidate onSubmit={handleStep} ref={formRef} className="relative flex h-full flex-col bg-surface">
      <div className="relative grow overflow-y-auto overflow-x-hidden">

        {/* MAIN TITLE */}
        <Transition
          as={Fragment}
          show={!showDetails}
          enter="transform transition ease-in-out duration-200"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transform transition ease-in-out duration-200"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
          unmount={false}
        >
          <div className="flex items-center border-b-6 px-4 py-4.5 lg:px-5">
            {/* Title */}
            <h4 className="flex grow items-center gap-2.5 text-xl font-bold">
              {/* Close drawer button */
                toggleDrawer && (
                  <Button
                    styling="none"
                    className="flex size-7 shrink-0 items-center justify-center rounded-full border"
                    onClick={() => toggleDrawer()}
                    {...dataTestId('cvSearchPage.filters.close')}
                  >
                    <XMarkIcon className="size-4.5" />
                  </Button>
                )
              }
              <span>{t('search.filters.title')}</span>
            </h4>

            { /* Delete button */
              Object?.keys(queryParams)?.length > 0 && (
                <Button
                  styling="text"
                  size="sm"
                  className="shrink-0 !font-bold"
                  onClick={resetAllFilters}
                  {...dataTestId('cvSearchPage.filters.clearAll')}
                >
                  {t('filters.delete-all.label')}
                </Button>
              )
            }
          </div>
        </Transition>

        { // LOCATION group
          showFilters?.location && (
            <SidebarGroup
              title={t('global.location.label')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.location)}
              clearGroup={() => setFormValues({ location: '', radiusInKm: '' })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.location')}
            >
              <LocationForm {...{ ...customFormsProps?.location, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // SEARCH IN group
          showFilters?.searchIn && (
            <SidebarGroup
              title={t('global.filter.search-in')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.searchIn, t)}
              clearGroup={() => setFormValues({
                nameSearch: '', queryFields: '', presentExperience: '',
              })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.searchIn')}
            >
              <SearchInForm {...{ ...customFormsProps?.searchIn, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // CONFINE RESULTS group
          showFilters?.display && (
            <SidebarGroup
              title={t('filters.groups.confine.title')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.confineResults, t)}
              clearGroup={() => setFormValues({
                searchInApplicants: '',
                searchInSavedApplicants: '',
                driversLicence: '',
                recommendedByAgent: '',
                excludeContacted: '',
                passedThroughTheInterview: '',
                videoInterviewOnly: '',
              })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.display')}
            >
              <ConfineResultsForm {...{ ...customFormsProps?.display, recordFunc: record, idPrefix: 'sidebar_' }} jobBasicInfo={jobBasicInfo} />
            </SidebarGroup>
          )
        }

        { // LAST ACTIVE group
          showFilters?.lastActive && (
            <SidebarGroup
              title={t('global.active-in.label')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.lastActivity, t)}
              clearGroup={() => setFormValues({ lastActivity: '' })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.lastActive')}
            >
              <LastActivityForm {...{ ...customFormsProps?.lastActive, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // SPOKEN LANGUAGES group
          showFilters?.spokenLanguages && (
            <SidebarGroup
              title={t('global.filter.spoken_lang')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.spokenLanguages, t)}
              clearGroup={() => setFormValues({ spokenLanguages: '' })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.spokenLanguages')}
            >
              <SpokenLanguagesForm {...{ ...customFormsProps?.spokenLanguages, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // CAREER LEVELS group
          showFilters?.careerLevels && (
            <SidebarGroup
              title={t('global.experience-level.label')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.careerLevels, t)}
              clearGroup={() => setFormValues({ careerLevels: '' })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.careerLevels')}
            >
              <CareerLevelsForm {...{ ...customFormsProps?.careerLevels, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // DOMAINS group
          showFilters?.domains && (
            <SidebarGroup
              title={t('global.filter.domain')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.domains, t)}
              clearGroup={() => setFormValues({ domains: '' })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.domain')}
            >
              <DomainsForm {...{ ...customFormsProps?.domains, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

        { // STUDY LEVELS group
          showFilters?.studyLevels && (
            <SidebarGroup
              title={t('global.studies.label')}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              activeFilters={mergeFilterValues(talentSearchLabels.studyLevels, t)}
              clearGroup={() => setFormValues({
                studyLevels: '', studySpecialization: '', graduateStart: '', graduateEnd: '',
              })}
              submitFunc={submitForm}
              {...dataTestId('cvSearchPage.filters.studyLevels')}
            >
              <StudyLevelsForm {...{ ...customFormsProps?.studyLevels, recordFunc: record, idPrefix: 'sidebar_' }} />
            </SidebarGroup>
          )
        }

      </div>

      {/* Submit button */}
      <div className="shrink-0 border-t-6 p-4 lg:px-6">
        <Button
          type="submit"
          color="secondary"
          rounding="full"
          className="w-full !px-8 sm:w-auto"
          onClick={() => {
            // close the drawer if we're not in a filter category group
            if (!showDetails && toggleDrawer) toggleDrawer();
            setShowDetails(false);
          }}
          {...dataTestId('cvSearchPage.filters.submit')}
        >
          {t('global.action.apply_filters')}
        </Button>
      </div>

    </form>
  );
};
